// Каналы входа одного сообщения: что реально ушло в генерацию — кадры с ролями,
// элементы, референсы. Режим резолвится из снимка запроса (`generationMode` +
// `modelName`), потому что роль кадра хранится позиционно и без описания режима
// не читается. Режим не нашёлся (историческая джоба, модель отключили) — кадры
// показываются без ролевых подписей, а не пропадают.

import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { collectMessageInputChannels, nonChannelAttachments } from "~/entities/chat";
import { useGenerationSettings } from "~/entities/model";
import { findModeConfig, getModelById } from "~/shared/data/chat-models";
import { toGenerationMode } from "~/shared/lib/generation-mode";

import type { ChatAttachment, LegacyGenerationJob, MessageInputChannelView } from "~/entities/chat";
import type { ArtifactType } from "~/shared/data/chat-models";

export interface MessageInputsView {
    channels: MessageInputChannelView[];
    /** Вложения вне каналов (аудио, документы, видео) — их рисует общий блок файлов. */
    otherFiles: ChatAttachment[];
    /**
     * Что порождала генерация — от этого зависит подпись канала кадров: у видео это
     * кадр, у изображения та же картинка означает исходник правки. `null` — модель
     * не резолвится (историческая джоба), тогда остаётся нейтральная подпись.
     */
    artifactType: ArtifactType | null;
}

export function useMessageInputs(
    job: LegacyGenerationJob | null,
    attachments: readonly ChatAttachment[],
): MessageInputsView {
    const { t } = useTranslation("chat");
    // Справочник уже загружен промпт-баром — берётся из кеша React Query, лишнего
    // запроса на каждое сообщение нет.
    const settingsQuery = useGenerationSettings();
    const models = settingsQuery.data;

    return useMemo(() => {
        const payload = job?.requestPayload;
        if (!payload) return { channels: [], otherFiles: [...attachments], artifactType: null };

        const backendMode = typeof payload.generationMode === "string" ? payload.generationMode : null;
        const model = getModelById(models, job.modelName);
        const mode = backendMode && model ? findModeConfig(model, toGenerationMode(backendMode)) : undefined;

        return {
            channels: collectMessageInputChannels({
                t,
                attachments,
                payload,
                mode: mode ?? null,
            }),
            otherFiles: nonChannelAttachments(attachments, payload),
            artifactType: model?.artifactType ?? null,
        };
    }, [job, models, attachments, t]);
}
